'use client';
import React, {useState, useRef, useEffect} from 'react';
import cn from './style.module.scss';

interface Tab {
  title: string;
  content: React.ReactNode;
  bgColor?: string;
  bgImage?: string;
  containerBgImage?: string;
}

interface DynamicTabsProps {
  tabs: Tab[];
  className?: string;
  tabClass?: string;
  contentClass?: string;
  graphicsTopClass?: string;
  graphicsBottomClass?: string;
  activeClassName?: string;
  containerBgImage?: string;
  bgFlowClass?: string;
  contentGraphicShapeTop?: string;
  containerShapeGraphicsBottom?: string;
  showFlowIndicator?: boolean; // Optional - default is false
}

const DynamicTabs: React.FC<DynamicTabsProps> = ({
  tabs,
  className,
  tabClass,
  contentClass,
  graphicsTopClass,
  graphicsBottomClass,
  activeClassName,
  containerBgImage,
  bgFlowClass,
  contentGraphicShapeTop,
  containerShapeGraphicsBottom,
  showFlowIndicator = false, // <-- default to false
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [direction, setDirection] = useState<'left' | 'right'>('right');
  const [flowStyle, setFlowStyle] = useState({
    left: 0,
    width: 0,
    backgroundColor: tabs[0]?.bgColor || '#007bff',
  });
  const tabRefs = useRef<HTMLLIElement[]>([]);

  useEffect(() => {
    if (showFlowIndicator && tabRefs.current[activeTab]) {
      const {offsetLeft, offsetWidth} = tabRefs.current[activeTab];
      setFlowStyle({
        left: offsetLeft,
        width: offsetWidth,
        backgroundColor: tabs[activeTab]?.bgColor || '#007bff',
      });
    }
  }, [activeTab, tabs, showFlowIndicator]);

  const handleTabClick = (index: number) => {
    setDirection(index > activeTab ? 'right' : 'left');
    setActiveTab(index);
  };

  return (
    <div className={`${cn.tabWrapper} ${className}`}>
      <div className={`${cn.tabNavigation} ${tabClass}`}>
        <ul className={cn.tabsList}>
          {showFlowIndicator && (
            <span
              className={`${cn.flowingBackground} ${bgFlowClass}`}
              style={flowStyle}
            />
          )}
          {tabs.map((tab, index) => (
            <li
              key={index}
              ref={(el) => (tabRefs.current[index] = el!)}
              onClick={() => handleTabClick(index)}>
              <a
                className={`${cn.tabLink} ${
                  activeTab === index
                    ? `${activeClassName} ${cn['activeLink']}`
                    : ''
                }`}>
                {tab.title}
              </a>
            </li>
          ))}
        </ul>
      </div>

      <div className={cn.tabContentWrapper}>
        <div
          className={`${cn.tabContentBlock} ${contentClass}`}
          style={{
            backgroundImage: containerBgImage
              ? `url(${containerBgImage})`
              : 'none',
          }}>
          {tabs.map((tab, index) => (
            <div
              key={index}
              className={`${cn.tabContent} ${
                index === activeTab
                  ? direction === 'right'
                    ? cn['slideInRight']
                    : cn['slideInLeft']
                  : ''
              }`}
              style={{display: index === activeTab ? 'block' : 'none'}}>
              {tab.content}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DynamicTabs;
